import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ADDR_KEYS = ['address1', 'address2', 'city', 'province', 'zip', 'country', 'phone'];

const EditOrderDialog = ({ open, onClose, order, onSaved, formatCurrency }) => {
  const [form, setForm] = useState({});
  const [items, setItems] = useState([]);
  const [addrIsObject, setAddrIsObject] = useState(false);
  const [addrFields, setAddrFields] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && order) {
      const sa = order.shipping_address;
      const saFull = order.shipping_address_full;
      const addrSrc = (sa && typeof sa === 'object') ? sa : ((saFull && typeof saFull === 'object') ? saFull : null);
      const isObj = !!addrSrc;
      setAddrIsObject(isObj);
      setAddrFields(isObj ? ADDR_KEYS.reduce((acc, k) => ({ ...acc, [k]: addrSrc[k] || '' }), {}) : {});
      setForm({
        customer_name: order.customer_name || '',
        customer_email: order.customer_email || '',
        customer_phone: order.customer_phone || (addrSrc && addrSrc.phone) || '',
        shipping_address: isObj ? '' : (typeof sa === 'string' ? sa : ''),
        shipping_cost: order.shipping_cost ?? 0,
        discount_amount: order.discount_amount ?? 0,
        tax: order.tax ?? 0,
        tax_inclusive: !!order.tax_inclusive,
        notes: order.notes || ''
      });
      setItems((order.items || []).map(it => ({ ...it, title: it.title || it.name || '', quantity: it.quantity ?? 1, price: it.price ?? 0 })));
    }
    // Re-initialize only when the dialog opens or a different order is shown —
    // background refreshes of the same order must not wipe in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order?.id]);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const setAddrField = (k, v) => setAddrFields(prev => ({ ...prev, [k]: v }));

  // Product search (same source as the Create Order flow)
  const [productResults, setProductResults] = useState([]);
  const [activeProductIdx, setActiveProductIdx] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const searchTimer = useRef(null);

  const searchSeq = useRef(0);
  const searchProducts = (term, idx) => {
    setActiveProductIdx(idx);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!term || term.length < 2) { setProductResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      const seq = ++searchSeq.current;
      setLoadingProducts(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE_URL}/enterprise/inventory`, {
          params: { search: term, limit: 10 },
          headers: { Authorization: `Bearer ${token}` }
        });
        if (seq === searchSeq.current) setProductResults(res.data.items || []);
      } catch (e) {
        if (seq === searchSeq.current) setProductResults([]);
      } finally {
        if (seq === searchSeq.current) setLoadingProducts(false);
      }
    }, 250);
  };

  const selectProduct = (product, idx) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      // swapping to a different product: drop old Shopify line identity so the
      // sync replaces the line instead of only adjusting its quantity
      const { shopify_line_item_id, variant_id, product_id, ...rest } = it;
      const keepIds = product.sku && product.sku === it.sku;
      return {
        ...(keepIds ? it : rest),
        sku: product.sku,
        title: product.product_title || product.name || product.sku,
        price: product.selling_price ?? it.price ?? 0,
        image_url: product.image_url || product.image || ''
      };
    }));
    setProductResults([]);
    setActiveProductIdx(null);
  };
  const setItem = (idx, k, v) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, [k]: v } : it));
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
  const addItem = () => setItems(prev => [...prev, { title: '', sku: '', quantity: 1, price: 0 }]);

  const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
  const subtotal = items.reduce((s, it) => s + num(it.price) * (parseInt(it.quantity) || 0), 0);
  const total = subtotal + num(form.shipping_cost) + (form.tax_inclusive ? 0 : num(form.tax)) - num(form.discount_amount);
  const fmt = (v) => formatCurrency ? formatCurrency(v) : v.toFixed(2);

  const handleSave = async () => {
    if (!form.customer_name?.trim()) { toast.error('Customer name is required'); return; }
    if (addrIsObject) {
      if (!addrFields.address1?.trim() && !addrFields.city?.trim()) { toast.error('Shipping address needs at least a street or city'); return; }
    } else if (!form.shipping_address?.trim()) { toast.error('Shipping address is required'); return; }
    if (items.length === 0) { toast.error('Order must have at least one item'); return; }
    for (const it of items) {
      if (!it.title?.trim()) { toast.error('Each item needs a name'); return; }
      if ((parseInt(it.quantity) || 0) < 1) { toast.error('Item quantity must be at least 1'); return; }
      if (num(it.price) < 0) { toast.error('Item price cannot be negative'); return; }
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email?.trim() || null,
        customer_phone: form.customer_phone?.trim() || null,
        items: items.map(it => ({ ...it, quantity: parseInt(it.quantity) || 1, price: num(it.price) })),
        shipping_cost: num(form.shipping_cost),
        discount_amount: num(form.discount_amount),
        tax: num(form.tax),
        tax_inclusive: !!form.tax_inclusive,
        notes: form.notes || null
      };
      if (addrIsObject) {
        const src = (order.shipping_address && typeof order.shipping_address === 'object')
          ? order.shipping_address
          : (order.shipping_address_full && typeof order.shipping_address_full === 'object' ? order.shipping_address_full : {});
        const changed = ADDR_KEYS.some(k => (addrFields[k] || '') !== (src[k] || ''));
        if (changed || typeof order.shipping_address !== 'object') {
          payload.shipping_address = ADDR_KEYS.reduce((acc, k) => ({ ...acc, [k]: addrFields[k] || '' }), {});
        }
      } else if (form.shipping_address.trim() !== (typeof order.shipping_address === 'string' ? order.shipping_address : '')) {
        payload.shipping_address = form.shipping_address.trim();
      }
      const res = await axios.put(`${API_BASE_URL}/orders/${order.id}/details`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sync = res.data.shopify_sync;
      if (sync?.success) {
        toast.success('Order updated & synced to Shopify');
      } else if (sync && !sync.success) {
        toast.success('Order updated');
        toast.warning(`Shopify sync failed: ${sync.error || 'unknown error'}`);
      } else {
        toast.success('Order updated');
      }
      (sync?.warnings || []).forEach(w => toast.warning(w));
      onSaved && onSaved(res.data.order);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-wt-paper border-wt-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-order-dialog">
        <DialogHeader>
          <DialogTitle className="text-wt-text-primary flex items-center gap-2">
            <Pencil className="w-4 h-4 text-wt-gold" />
            Edit Order {order?.order_number}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <p className="text-xs uppercase tracking-wider text-wt-text-secondary mb-2">Customer</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-wt-text-secondary">Name *</Label>
                <Input value={form.customer_name || ''} onChange={e => setField('customer_name', e.target.value)}
                  className="bg-wt-subtle border-wt-border text-wt-text-primary mt-1" data-testid="edit-order-customer-name" />
              </div>
              <div>
                <Label className="text-xs text-wt-text-secondary">Email</Label>
                <Input value={form.customer_email || ''} onChange={e => setField('customer_email', e.target.value)}
                  className="bg-wt-subtle border-wt-border text-wt-text-primary mt-1" data-testid="edit-order-customer-email" />
              </div>
              <div>
                <Label className="text-xs text-wt-text-secondary">Phone</Label>
                <Input value={form.customer_phone || ''} onChange={e => setField('customer_phone', e.target.value)}
                  className="bg-wt-subtle border-wt-border text-wt-text-primary mt-1" data-testid="edit-order-customer-phone" />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs text-wt-text-secondary">Shipping Address *</Label>
            {addrIsObject ? (
              <div className="grid grid-cols-2 gap-2 mt-1" data-testid="edit-order-shipping-address">
                <Input placeholder="Address line 1" value={addrFields.address1 || ''} onChange={e => setAddrField('address1', e.target.value)}
                  className="col-span-2 bg-wt-subtle border-wt-border text-wt-text-primary h-8 text-sm" data-testid="edit-order-addr-address1" />
                <Input placeholder="Address line 2" value={addrFields.address2 || ''} onChange={e => setAddrField('address2', e.target.value)}
                  className="col-span-2 bg-wt-subtle border-wt-border text-wt-text-primary h-8 text-sm" data-testid="edit-order-addr-address2" />
                <Input placeholder="City" value={addrFields.city || ''} onChange={e => setAddrField('city', e.target.value)}
                  className="bg-wt-subtle border-wt-border text-wt-text-primary h-8 text-sm" data-testid="edit-order-addr-city" />
                <Input placeholder="State / Province" value={addrFields.province || ''} onChange={e => setAddrField('province', e.target.value)}
                  className="bg-wt-subtle border-wt-border text-wt-text-primary h-8 text-sm" data-testid="edit-order-addr-province" />
                <Input placeholder="ZIP / Postal code" value={addrFields.zip || ''} onChange={e => setAddrField('zip', e.target.value)}
                  className="bg-wt-subtle border-wt-border text-wt-text-primary h-8 text-sm" data-testid="edit-order-addr-zip" />
                <Input placeholder="Country" value={addrFields.country || ''} onChange={e => setAddrField('country', e.target.value)}
                  className="bg-wt-subtle border-wt-border text-wt-text-primary h-8 text-sm" data-testid="edit-order-addr-country" />
                <Input placeholder="Address phone" value={addrFields.phone || ''} onChange={e => setAddrField('phone', e.target.value)}
                  className="col-span-2 bg-wt-subtle border-wt-border text-wt-text-primary h-8 text-sm" data-testid="edit-order-addr-phone" />
              </div>
            ) : (
              <Textarea value={form.shipping_address || ''} onChange={e => setField('shipping_address', e.target.value)}
                rows={2} className="bg-wt-subtle border-wt-border text-wt-text-primary mt-1" data-testid="edit-order-shipping-address" />
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-wider text-wt-text-secondary">Items</p>
              <Button variant="outline" size="sm" onClick={addItem}
                className="h-7 text-xs border-wt-border text-wt-text-primary" data-testid="edit-order-add-item-btn">
                <Plus className="w-3 h-3 mr-1" /> Add Item
              </Button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-wt-subtle/50 border border-wt-border rounded-md p-2" data-testid={`edit-order-item-row-${idx}`}>
                  <div className="col-span-12 sm:col-span-5 relative">
                    <Label className="text-[10px] text-wt-text-secondary">Name</Label>
                    <Input value={it.title}
                      onChange={e => { setItem(idx, 'title', e.target.value); searchProducts(e.target.value, idx); }}
                      onBlur={() => setTimeout(() => { setProductResults([]); setActiveProductIdx(null); }, 200)}
                      placeholder="Type to search products..."
                      className="bg-wt-paper border-wt-border text-wt-text-primary h-8 text-sm mt-0.5" data-testid={`edit-order-item-title-${idx}`} />
                    {activeProductIdx === idx && (productResults.length > 0 || loadingProducts) && (
                      <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-wt-paper border border-wt-border rounded-md shadow-lg max-h-56 overflow-y-auto" data-testid="edit-order-product-results">
                        {loadingProducts && <div className="px-3 py-2 text-xs text-wt-text-muted">Searching...</div>}
                        {productResults.map((p) => (
                          <button key={p.sku} type="button"
                            className="w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-wt-subtle"
                            onMouseDown={(e) => { e.preventDefault(); selectProduct(p, idx); }}
                            data-testid={`edit-order-product-option-${p.sku}`}>
                            {(p.image_url || p.image) && (
                              <img src={p.image_url || p.image} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                            )}
                            <span className="flex-1 min-w-0">
                              <span className="block text-xs text-wt-text-primary truncate">{p.product_title || p.name}</span>
                              <span className="block text-[10px] text-wt-text-muted">{p.sku}</span>
                            </span>
                            <span className="text-xs text-wt-text-secondary whitespace-nowrap">{p.selling_price != null ? (formatCurrency ? formatCurrency(p.selling_price) : p.selling_price) : ''}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <Label className="text-[10px] text-wt-text-secondary">SKU</Label>
                    <Input value={it.sku || ''} onChange={e => setItem(idx, 'sku', e.target.value)}
                      className="bg-wt-paper border-wt-border text-wt-text-primary h-8 text-sm mt-0.5" data-testid={`edit-order-item-sku-${idx}`} />
                  </div>
                  <div className="col-span-3 sm:col-span-2">
                    <Label className="text-[10px] text-wt-text-secondary">Qty</Label>
                    <Input type="number" min="1" value={it.quantity} onChange={e => setItem(idx, 'quantity', e.target.value)}
                      className="bg-wt-paper border-wt-border text-wt-text-primary h-8 text-sm mt-0.5" data-testid={`edit-order-item-qty-${idx}`} />
                  </div>
                  <div className="col-span-3 sm:col-span-2">
                    <Label className="text-[10px] text-wt-text-secondary">Price</Label>
                    <Input type="number" min="0" step="0.01" value={it.price} onChange={e => setItem(idx, 'price', e.target.value)}
                      className="bg-wt-paper border-wt-border text-wt-text-primary h-8 text-sm mt-0.5" data-testid={`edit-order-item-price-${idx}`} />
                  </div>
                  <div className="col-span-2 sm:col-span-1 flex justify-end">
                    <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} disabled={items.length === 1}
                      className="h-8 w-8 p-0 text-red-400 hover:bg-red-500/10" data-testid={`edit-order-item-remove-${idx}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-wt-text-secondary">Shipping</Label>
              <Input type="number" min="0" step="0.01" value={form.shipping_cost} onChange={e => setField('shipping_cost', e.target.value)}
                className="bg-wt-subtle border-wt-border text-wt-text-primary mt-1" data-testid="edit-order-shipping-cost" />
            </div>
            <div>
              <Label className="text-xs text-wt-text-secondary">Discount</Label>
              <Input type="number" min="0" step="0.01" value={form.discount_amount} onChange={e => setField('discount_amount', e.target.value)}
                className="bg-wt-subtle border-wt-border text-wt-text-primary mt-1" data-testid="edit-order-discount" />
            </div>
            <div>
              <Label className="text-xs text-wt-text-secondary">Tax</Label>
              <Input type="number" min="0" step="0.01" value={form.tax} onChange={e => setField('tax', e.target.value)}
                className="bg-wt-subtle border-wt-border text-wt-text-primary mt-1" data-testid="edit-order-tax" />
              <select
                value={form.tax_inclusive ? 'incl' : 'excl'}
                onChange={e => setField('tax_inclusive', e.target.value === 'incl')}
                className="mt-1 w-full bg-wt-subtle border border-wt-border rounded-md text-xs text-wt-text-primary p-1.5"
                data-testid="edit-order-tax-mode">
                <option value="excl">Tax excluded — added on top</option>
                <option value="incl">Tax included in prices</option>
              </select>
            </div>
          </div>

          <div>
            <Label className="text-xs text-wt-text-secondary">Notes</Label>
            <Textarea value={form.notes || ''} onChange={e => setField('notes', e.target.value)}
              rows={2} className="bg-wt-subtle border-wt-border text-wt-text-primary mt-1" data-testid="edit-order-notes" />
          </div>

          <div className="bg-wt-subtle/60 border border-wt-border rounded-md p-3 space-y-1 text-sm" data-testid="edit-order-totals">
            <div className="flex justify-between text-wt-text-secondary"><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
            <div className="flex justify-between text-wt-text-secondary"><span>Shipping</span><span>{fmt(num(form.shipping_cost))}</span></div>
            <div className="flex justify-between text-wt-text-secondary"><span>Tax{form.tax_inclusive ? ' (included)' : ''}</span><span>{fmt(num(form.tax))}</span></div>
            <div className="flex justify-between text-wt-text-secondary"><span>Discount</span><span>-{fmt(num(form.discount_amount))}</span></div>
            <div className="flex justify-between font-semibold text-wt-text-primary pt-1 border-t border-wt-border"><span>New Total</span><span data-testid="edit-order-new-total">{fmt(total)}</span></div>
          </div>

          {(order?.source === 'shopify' || order?.shopify_order_id) ? (
            <p className="text-xs text-emerald-400">Customer, address, email and notes sync to the linked Shopify order on save. Item and price changes stay in Opswarex only.</p>
          ) : null}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} className="border-wt-border text-wt-text-primary" data-testid="edit-order-cancel-btn">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-wt-gold text-black hover:bg-wt-gold/90" data-testid="edit-order-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditOrderDialog;
